import type { Block, LayoutFn, Line } from "./base";
import { padLine, wrapText } from "./text";

export type TreeNode = {
  label: string;
  onClick?: () => void;
  children?: TreeNode[];
};

export function tree(
  data: TreeNode[],
  opts?: { color?: string; branchColor?: string; leafColor?: string },
): LayoutFn {
  return (w) => {
    const result: Block = [];
    function walk(nodes: TreeNode[], prefix: string) {
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]!;
        const last = i === nodes.length - 1;
        const hasKids = node.children && node.children.length > 0;
        const connector = last ? "\u2514\u2500\u2500 " : "\u251c\u2500\u2500 ";
        const line: Line = [];
        if (prefix) line.push({ text: prefix, color: opts?.branchColor ?? opts?.color, noSelect: true });
        line.push({ text: connector, color: opts?.branchColor ?? opts?.color, noSelect: true });
        line.push({ text: node.label, color: hasKids ? opts?.color : (opts?.leafColor ?? opts?.color), onClick: node.onClick });
        result.push(padLine(line, w));
        if (hasKids) {
          walk(node.children!, prefix + (last ? "    " : "\u2502   "));
        }
      }
    }
    walk(data, "");
    return result;
  };
}

export type ListItem = string | { text: string; color?: string; onClick?: () => void };

export function list(
  items: ListItem[],
  opts?: { ordered?: boolean; bulletColor?: string; color?: string },
): LayoutFn {
  return (w) => {
    const result: Block = [];
    const ordered = opts?.ordered ?? false;
    const digits = String(items.length).length;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const text = typeof item === "string" ? item : item.text;
      const itemColor = typeof item === "string" ? opts?.color : (item.color ?? opts?.color);
      const itemClick = typeof item === "string" ? undefined : item.onClick;
      const prefix = ordered
        ? ` ${String(i + 1).padStart(digits, " ")}. `
        : " * ";
      const indent = " ".repeat(prefix.length);
      const wrapped = wrapText(text, w - prefix.length);
      for (let j = 0; j < wrapped.length; j++) {
        const line: Line = [];
        if (j === 0) {
          line.push({ text: prefix, color: opts?.bulletColor });
        } else {
          line.push(indent);
        }
        line.push(itemColor || itemClick ? { text: wrapped[j]!, color: itemColor, onClick: itemClick } : wrapped[j]!);
        result.push(padLine(line, w));
      }
    }
    return result;
  };
}
